import * as faceapi from 'face-api.js'

const FACE_MODEL_CDN_BASE = 'https://justadudewhohacks.github.io/face-api.js/models'
const DETECT_INTERVAL_MS = 350
const REQUIRED_EYE_FRAMES = 5
const CAMERA_INIT_TIMEOUT_MS = 7000
const CAPTURE_WIDTH = 900
const CAPTURE_HEIGHT = 1200
const PHOTO_MIN_SIZE_KB = 60
const PHOTO_MAX_SIZE_KB = 200
const MIN_FACE_BRIGHTNESS = 52
const OVAL_SEMI_AXIS_X_FRAC = 0.245
const OVAL_SEMI_AXIS_Y_FRAC = 0.42
const BOX_INSET_X_FRAC = 0.1
const BOX_INSET_Y_FRAC = 0.06
const MIN_FACE_WIDTH_FRAC = 0.17
const MIN_FACE_HEIGHT_FRAC = 0.14
const MAX_FACE_WIDTH_FRAC = 0.72
const MAX_FACE_HEIGHT_FRAC = 0.78

export default {
  data () {
    return {
      cameraDialog: false,
      cameraEnabled: false,
      cameraStream: null,
      cameraVideoReady: false,
      cameraInitTimeoutId: null,
      faceDetectionReady: false,
      faceDetectionLoading: false,
      faceDetectionError: '',
      faceDetectionIntervalId: null,
      hintText: 'Detectando rostro…',
      faceReady: false,
      eyeOpenFrames: 0,
      isCountingDown: false,
      countdown: 0,
      countdownTimerId: null
    }
  },
  beforeDestroy () {
    this.stopCamera()
  },
  watch: {
    cameraDialog (isOpen) {
      if (!isOpen) {
        this.stopCamera()
      }
    }
  },
  methods: {
    getCameraAccessErrorMessage (error) {
      const code = error && (error.name || error.code || '')
      if (code === 'NotAllowedError' || code === 'PermissionDeniedError' || code === 'SecurityError') {
        return 'Es necesario activar cámara para proceder con la toma de foto'
      }
      if (code === 'NotFoundError' || code === 'DevicesNotFoundError') {
        return 'No se encontró una cámara disponible en este dispositivo'
      }
      if (code === 'NotReadableError' || code === 'TrackStartError' || code === 'AbortError') {
        return 'Ocurrió un error al inicializar la cámara. Verifique el dispositivo e intente de nuevo'
      }
      return 'No se pudo activar la cámara. Revisa permisos del navegador.'
    },
    canCaptureFromCamera () {
      return this.faceDetectionReady && !this.faceDetectionLoading && this.faceReady && this.cameraVideoReady
    },
    getFaceDetectionStatusLabel () {
      if (!this.cameraEnabled) {
        return ''
      }
      if (this.faceDetectionLoading) {
        return 'Cargando detección facial...'
      }
      if (this.faceDetectionError) {
        return this.faceDetectionError
      }
      if (!this.cameraVideoReady) {
        return 'Iniciando cámara...'
      }
      if (this.countdown > 0) {
        return this.countdown === 1 ? 'Capturando…' : ('Captura en ' + this.countdown + '…')
      }
      if (this.faceReady) {
        return 'No se mueva'
      }
      return this.hintText
    },
    isJpegExtension (extension) {
      return extension === 'jpg' || extension === 'jpeg'
    },
    isPhotoSizeInRangeKb (sizeKb) {
      return sizeKb >= PHOTO_MIN_SIZE_KB && sizeKb <= PHOTO_MAX_SIZE_KB
    },
    getPhotoSizeValidationMessage () {
      return 'El tamaño de la foto debe estar entre 60KB y 200KB'
    },
    resetCameraFaceState () {
      this.faceDetectionError = ''
      this.hintText = 'Detectando rostro…'
      this.faceReady = false
      this.eyeOpenFrames = 0
      this.isCountingDown = false
      this.countdown = 0
      this.clearCountdownTimer()
      this.cameraVideoReady = false
    },
    clearCountdownTimer () {
      if (this.countdownTimerId) {
        clearInterval(this.countdownTimerId)
      }
      this.countdownTimerId = null
    },
    cancelCountdown (message) {
      this.clearCountdownTimer()
      this.isCountingDown = false
      this.countdown = 0
      this.faceReady = false
      this.eyeOpenFrames = 0
      this.hintText = message
    },
    async ensureFaceApiModelsLoaded () {
      if (this.faceDetectionReady) {
        return true
      }
      this.faceDetectionLoading = true
      this.faceDetectionError = ''
      const tryLoad = async (base) => {
        await faceapi.nets.tinyFaceDetector.loadFromUri(base)
        await faceapi.nets.faceLandmark68TinyNet.loadFromUri(base)
      }
      try {
        await tryLoad(FACE_MODEL_CDN_BASE)
        this.faceDetectionReady = true
        return true
      } catch (eCdn) {
        this.faceDetectionError = 'No se pudo iniciar la detección facial'
        this.faceDetectionReady = false
        return false
      } finally {
        this.faceDetectionLoading = false
      }
    },
    stopFaceDetectionLoop () {
      if (this.faceDetectionIntervalId) {
        clearInterval(this.faceDetectionIntervalId)
      }
      this.faceDetectionIntervalId = null
    },
    pointInFaceOvalEllipse (px, py, video) {
      const cx = video.videoWidth / 2
      const cy = video.videoHeight / 2
      const bx = Math.max(1, video.videoWidth * OVAL_SEMI_AXIS_X_FRAC)
      const ay = Math.max(1, video.videoHeight * OVAL_SEMI_AXIS_Y_FRAC)
      const dx = (px - cx) / bx
      const dy = (py - cy) / ay
      return (dx * dx + dy * dy) <= 1
    },

    faceCoreFitsInsideOval (box, video) {
      const cx = box.x + box.width / 2
      const cy = box.y + box.height / 2
      if (!this.pointInFaceOvalEllipse(cx, cy, video)) {
        return false
      }
      const ix = box.x + (box.width * BOX_INSET_X_FRAC) / 2
      const iy = box.y + (box.height * BOX_INSET_Y_FRAC) / 2
      const iw = box.width * (1 - BOX_INSET_X_FRAC)
      const ih = box.height * (1 - BOX_INSET_Y_FRAC)
      if (iw < 1 || ih < 1) {
        return false
      }
      const corners = [
        { x: ix, y: iy },
        { x: ix + iw, y: iy },
        { x: ix, y: iy + ih },
        { x: ix + iw, y: iy + ih }
      ]
      const cornersInside = corners.filter((c) => this.pointInFaceOvalEllipse(c.x, c.y, video)).length
      return cornersInside >= 3
    },
    isFaceCenteredAndStraight (detection, video) {
      const box = detection.detection.box
      const landmarks = detection.landmarks
      const vw = video.videoWidth
      const vh = video.videoHeight

      if (box.width < vw * MIN_FACE_WIDTH_FRAC || box.height < vh * MIN_FACE_HEIGHT_FRAC) {
        return false
      }
      if (box.width > vw * MAX_FACE_WIDTH_FRAC || box.height > vh * MAX_FACE_HEIGHT_FRAC) {
        return false
      }
      if (!this.faceCoreFitsInsideOval(box, video)) {
        return false
      }

      const leftEye = landmarks.getLeftEye()
      const rightEye = landmarks.getRightEye()
      const nose = landmarks.getNose()
      const eyesCenterX = ((leftEye[0].x + leftEye[3].x) / 2 + (rightEye[0].x + rightEye[3].x) / 2) / 2
      return Math.abs(nose[3].x - eyesCenterX) < box.width * 0.18
    },
    eyesOpen (detection) {
      const d = (p1, p2) => Math.hypot(p1.x - p2.x, p1.y - p2.y)
      const ear = (eye) => ((d(eye[1], eye[5]) + d(eye[2], eye[4])) / (2 * d(eye[0], eye[3])))
      return (ear(detection.landmarks.getLeftEye()) > 0.24 && ear(detection.landmarks.getRightEye()) > 0.24)
    },
    hasEnoughFaceLighting (video, box) {
      var sx = Math.max(0, Math.floor(box.x))
      var sy = Math.max(0, Math.floor(box.y))
      var sw = Math.min(video.videoWidth - sx, Math.max(1, Math.floor(box.width)))
      var sh = Math.min(video.videoHeight - sy, Math.max(1, Math.floor(box.height)))
      if (sw < 2 || sh < 2) {
        return false
      }

      var sampleCanvas = document.createElement('canvas')
      sampleCanvas.width = 64
      sampleCanvas.height = 64
      var sampleCtx = sampleCanvas.getContext('2d')
      sampleCtx.drawImage(video, sx, sy, sw, sh, 0, 0, sampleCanvas.width, sampleCanvas.height)
      var pixels = sampleCtx.getImageData(0, 0, sampleCanvas.width, sampleCanvas.height).data
      var total = 0
      var count = 0
      for (var i = 0; i < pixels.length; i += 4) {
        var r = pixels[i]
        var g = pixels[i + 1]
        var b = pixels[i + 2]
        total += (0.2126 * r) + (0.7152 * g) + (0.0722 * b)
        count++
      }
      var avgLuma = count ? (total / count) : 0
      return avgLuma >= MIN_FACE_BRIGHTNESS
    },
    resetFaceHint (message) {
      this.faceReady = false
      this.hintText = message
      this.eyeOpenFrames = 0
    },
    startFaceDetectionLoop () {
      this.stopFaceDetectionLoop()
      const options = new faceapi.TinyFaceDetectorOptions({
        inputSize: 320,
        scoreThreshold: 0.5
      })
      this.faceDetectionIntervalId = setInterval(async () => {
        if (!this.cameraEnabled || !this.faceDetectionReady || this.faceDetectionLoading) {
          return
        }
        const video = this.$refs.cameraVideo
        if (!video || video.paused || video.ended || video.readyState < 2) {
          return
        }
        try {
          const detections = await faceapi
            .detectAllFaces(video, options)
            .withFaceLandmarks(true)
          const facesCount = detections ? detections.length : 0

          if (this.isCountingDown) {
            if (facesCount !== 1) {
              this.cancelCountdown(facesCount > 1 ? 'Solo debe aparecer un rostro en cámara' : 'Debe permanecer fijo y mirando a la cámara')
              return
            }
            const detectionDuringCountdown = detections[0]
            if (!this.isFaceCenteredAndStraight(detectionDuringCountdown, video) ||
              !this.eyesOpen(detectionDuringCountdown) ||
              !this.hasEnoughFaceLighting(video, detectionDuringCountdown.detection.box)) {
              this.cancelCountdown('Debe permanecer fijo y mirando a la cámara')
            }
            return
          }

          if (facesCount === 0) {
            this.resetFaceHint('Coloque su rostro frente a la cámara')
            return
          }
          if (facesCount > 1) {
            this.resetFaceHint('Solo debe aparecer un rostro en cámara')
            return
          }
          const detection = detections[0]

          if (!this.isFaceCenteredAndStraight(detection, video)) {
            this.resetFaceHint('Coloque el rostro dentro de la silueta')
            return
          }
          if (!this.hasEnoughFaceLighting(video, detection.detection.box)) {
            this.resetFaceHint('Mejore la iluminación del rostro')
            return
          }

          if (!this.eyesOpen(detection)) {
            this.resetFaceHint('Abra los ojos')
            return
          }

          this.eyeOpenFrames += 1
          if (this.eyeOpenFrames < REQUIRED_EYE_FRAMES) {
            this.faceReady = false
            this.hintText = 'Mantenga los ojos abiertos'
            return
          }

          this.faceReady = true
          this.hintText = 'No se mueva'
          this.startCountdown()
        } catch (error) {
          this.faceDetectionError = 'Error detectando rostro en cámara'
          this.faceReady = false
        }
      }, DETECT_INTERVAL_MS)
    },
    startCountdown () {
      if (this.isCountingDown) {
        return
      }
      this.isCountingDown = true
      this.countdown = 3
      this.clearCountdownTimer()
      this.countdownTimerId = setInterval(() => {
        this.countdown -= 1
        if (this.countdown <= 0) {
          this.clearCountdownTimer()
          this.isCountingDown = false
          this.countdown = 0
          this.performCameraCaptureFromVideo()
        }
      }, 1000)
    },
    onCameraVideoPlaying () {
      if (this.cameraInitTimeoutId) {
        clearTimeout(this.cameraInitTimeoutId)
        this.cameraInitTimeoutId = null
      }
      this.cameraVideoReady = true
      this.faceDetectionError = ''
      if (this.faceDetectionReady) {
        this.startFaceDetectionLoop()
      }
    },
    async onFileChange (e) {
      var files = e.target.files || e.dataTransfer.files
      if (!files || !files.length) {
        return
      }

      var selectedFile = files[0]
      var filePath = selectedFile.name || ''
      var extension = filePath.replace(/^.*\./, '').toLowerCase()
      var fileSize = parseInt(selectedFile.size / 1024)

      if (!this.isJpegExtension(extension)) {
        this.updateAlertMessage('Solo se admite el formato jpg o jpeg')
        this.updateIsInAlert(true)
        return
      }

      if (fileSize < PHOTO_MIN_SIZE_KB) {
        this.updateAlertMessage(this.getPhotoSizeValidationMessage())
        this.updateIsInAlert(true)
        return
      }

      if (fileSize > PHOTO_MAX_SIZE_KB) {
        try {
          var compressedResult = await this.compressFileToJpeg(selectedFile)
          if (!this.isPhotoSizeInRangeKb(compressedResult.sizeKb)) {
            this.updateAlertMessage(this.getPhotoSizeValidationMessage())
            this.updateIsInAlert(true)
            return
          }
          this.user.photo = compressedResult.dataUrl
          this.user_url_photo = compressedResult.dataUrl
        } catch (err) {
          this.updateAlertMessage('No se pudo procesar la imagen seleccionada')
          this.updateIsInAlert(true)
        }
        return
      }

      this.user_url_photo = URL.createObjectURL(selectedFile)
      this.createImage(selectedFile)
    },
    getDataUrlSizeKb (dataUrl) {
      var base64Part = (dataUrl || '').split(',')[1] || ''
      return parseInt((base64Part.length * 3 / 4) / 1024)
    },
    applyCaptureDetailBoost (sourceCanvas) {
      var target = document.createElement('canvas')
      target.width = sourceCanvas.width
      target.height = sourceCanvas.height
      var ctx = target.getContext('2d')
      ctx.filter = 'contrast(1.12) saturate(1.05)'
      ctx.drawImage(sourceCanvas, 0, 0)
      ctx.filter = 'none'
      return target
    },
    applyMicroNoiseForJpegSize (sourceCanvas) {
      var target = document.createElement('canvas')
      target.width = sourceCanvas.width
      target.height = sourceCanvas.height
      var ctx = target.getContext('2d')
      ctx.drawImage(sourceCanvas, 0, 0)
      var w = target.width
      var h = target.height
      var imageData = ctx.getImageData(0, 0, w, h)
      var d = imageData.data
      var step = Math.max(1, Math.floor((w * h) / 80000))
      for (var i = 0; i < d.length; i += 4 * step) {
        var delta = (Math.floor(Math.random() * 5) - 2)
        d[i] = Math.min(255, Math.max(0, d[i] + delta))
        d[i + 1] = Math.min(255, Math.max(0, d[i + 1] + delta))
        d[i + 2] = Math.min(255, Math.max(0, d[i + 2] + delta))
      }
      ctx.putImageData(imageData, 0, 0)
      return target
    },
    compressFileToJpeg (file) {
      return new Promise((resolve, reject) => {
        var reader = new FileReader()
        reader.onload = (event) => {
          var image = new Image()
          image.onload = () => {
            var canvas = document.createElement('canvas')
            canvas.width = image.width
            canvas.height = image.height
            var context = canvas.getContext('2d')
            context.drawImage(image, 0, 0, canvas.width, canvas.height)

            var quality = 0.9
            var dataUrl = canvas.toDataURL('image/jpeg', quality)
            var sizeKb = this.getDataUrlSizeKb(dataUrl)

            while (sizeKb > PHOTO_MAX_SIZE_KB && quality > 0.2) {
              quality = quality - 0.1
              dataUrl = canvas.toDataURL('image/jpeg', quality)
              sizeKb = this.getDataUrlSizeKb(dataUrl)
            }

            resolve({ dataUrl, sizeKb })
          }
          image.onerror = () => reject(new Error('invalid_image'))
          image.src = event.target.result
        }
        reader.onerror = () => reject(new Error('read_error'))
        reader.readAsDataURL(file)
      })
    },
    createImage (file) {
      var reader = new FileReader()
      var vm = this
      reader.readAsDataURL(file)
      reader.onload = function () {
        vm.user.photo = reader.result
      }
    },
    async openCamera () {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.updateAlertMessage('Este navegador no soporta acceso a camara')
        this.updateIsInAlert(true)
        return
      }

      // Cada intento de "Activar cámara" reinicia estado para forzar un nuevo request de permisos.
      this.stopCamera()
      this.resetCameraFaceState()
      this.cameraDialog = true

      const modelsLoaded = await this.ensureFaceApiModelsLoaded()
      if (!modelsLoaded) {
        this.updateAlertMessage('No se pudo iniciar la detección facial')
        this.updateIsInAlert(true)
        this.cameraDialog = false
        return
      }

      let stream
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'user' } }
        })
      } catch (e1) {
        try {
          stream = await navigator.mediaDevices.getUserMedia({ video: true })
        } catch (e2) {
          this.updateAlertMessage(this.getCameraAccessErrorMessage(e2))
          this.updateIsInAlert(true)
          this.cameraDialog = false
          return
        }
      }

      this.cameraStream = stream
      this.cameraEnabled = true
      this.faceDetectionError = ''

      this.$nextTick(() => {
        const video = this.$refs.cameraVideo
        if (video) {
          video.srcObject = stream
          video.autoplay = true
          video.muted = true
          video.setAttribute('playsinline', 'true')
          video.onloadedmetadata = () => {
            var playPromise = video.play()
            if (playPromise && typeof playPromise.catch === 'function') {
              playPromise.catch(() => {})
            }
          }
          var immediatePlayPromise = video.play()
          if (immediatePlayPromise && typeof immediatePlayPromise.catch === 'function') {
            immediatePlayPromise.catch(() => {})
          }
        }
      })

      this.cameraInitTimeoutId = setTimeout(() => {
        if (!this.cameraVideoReady) {
          this.faceDetectionError = 'Error al inicializar la cámara'
          this.updateAlertMessage('Ocurrió un error al inicializar la cámara. Verifique el dispositivo e intente de nuevo')
          this.updateIsInAlert(true)
          this.stopCamera()
          this.cameraDialog = true
        }
      }, CAMERA_INIT_TIMEOUT_MS)
    },
    performCameraCaptureFromVideo () {
      this.stopFaceDetectionLoop()
      this.clearCountdownTimer()

      const video = this.$refs.cameraVideo
      const canvas = this.$refs.cameraCaptureCanvas
      if (!video || !canvas || !video.videoWidth || !video.videoHeight) {
        this.updateAlertMessage('La cámara aún no está lista. Espera un momento e intenta de nuevo.')
        this.updateIsInAlert(true)
        return
      }

      const ctx = canvas.getContext('2d')
      canvas.width = CAPTURE_WIDTH
      canvas.height = CAPTURE_HEIGHT

      const targetRatio = canvas.width / canvas.height
      const videoRatio = video.videoWidth / video.videoHeight

      let sx, sy, sw, sh
      if (videoRatio > targetRatio) {
        sh = video.videoHeight
        sw = sh * targetRatio
        sx = (video.videoWidth - sw) / 2
        sy = 0
      } else {
        sw = video.videoWidth
        sh = sw / targetRatio
        sx = 0
        sy = (video.videoHeight - sh) / 2
      }

      ctx.drawImage(video, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height)

      var workingCanvas = canvas
      var quality = 0.92
      var dataUrl = workingCanvas.toDataURL('image/jpeg', quality)
      var sizeKb = this.getDataUrlSizeKb(dataUrl)
      while (sizeKb > PHOTO_MAX_SIZE_KB && quality > 0.25) {
        quality = quality - 0.05
        dataUrl = workingCanvas.toDataURL('image/jpeg', quality)
        sizeKb = this.getDataUrlSizeKb(dataUrl)
      }

      while (sizeKb < PHOTO_MIN_SIZE_KB && quality < 1) {
        quality = Math.min(1, quality + 0.05)
        dataUrl = workingCanvas.toDataURL('image/jpeg', quality)
        sizeKb = this.getDataUrlSizeKb(dataUrl)
      }

      if (sizeKb < PHOTO_MIN_SIZE_KB) {
        workingCanvas = this.applyCaptureDetailBoost(workingCanvas)
        quality = 1
        dataUrl = workingCanvas.toDataURL('image/jpeg', quality)
        sizeKb = this.getDataUrlSizeKb(dataUrl)
      }
      if (sizeKb < PHOTO_MIN_SIZE_KB) {
        workingCanvas = this.applyMicroNoiseForJpegSize(workingCanvas)
        dataUrl = workingCanvas.toDataURL('image/jpeg', 1)
        sizeKb = this.getDataUrlSizeKb(dataUrl)
      }
      while (sizeKb > PHOTO_MAX_SIZE_KB && quality > 0.25) {
        quality = quality - 0.05
        dataUrl = workingCanvas.toDataURL('image/jpeg', quality)
        sizeKb = this.getDataUrlSizeKb(dataUrl)
      }

      if (!this.isPhotoSizeInRangeKb(sizeKb)) {
        this.updateAlertMessage(this.getPhotoSizeValidationMessage())
        this.updateIsInAlert(true)
        this.resetFaceHint('Ajuste iluminación o distancia e intente de nuevo')
        if (this.faceDetectionReady && this.cameraEnabled && this.cameraVideoReady) {
          this.startFaceDetectionLoop()
        }
        return
      }

      this.user.photo = dataUrl
      this.user_url_photo = dataUrl
      this.cameraDialog = false
      this.stopCamera()
    },
    captureFromCamera () {
      if (!this.canCaptureFromCamera()) {
        this.updateAlertMessage(this.hintText || 'Ajuste el rostro frente a la cámara para capturar')
        this.updateIsInAlert(true)
        return
      }
      this.performCameraCaptureFromVideo()
    },
    closeCameraDialog () {
      this.clearCountdownTimer()
      this.isCountingDown = false
      this.countdown = 0
      this.cameraDialog = false
      this.stopCamera()
    },
    stopCamera () {
      this.clearCountdownTimer()
      this.isCountingDown = false
      this.countdown = 0
      this.stopFaceDetectionLoop()
      if (this.cameraInitTimeoutId) {
        clearTimeout(this.cameraInitTimeoutId)
        this.cameraInitTimeoutId = null
      }
      if (this.cameraStream && this.cameraStream.getTracks) {
        this.cameraStream.getTracks().forEach(track => track.stop())
      }
      this.cameraStream = null
      this.cameraEnabled = false
      const video = this.$refs.cameraVideo
      if (video) {
        video.srcObject = null
      }
      this.resetCameraFaceState()
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/views/users/cameraCaptureMixin.js